import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Plus, Search, X, Menu as MenuIcon, MapPin, Phone, Mail, User, ChevronDown, Check, CheckCircle2, QrCode, ShoppingCart, Bike, CalendarClock, Calendar, Trash2, Settings as SettingsIcon, Sparkles, Power, ChefHat, Percent, Landmark } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Toggle } from './Toggle';
import { useBranches, Branch, BranchOrderMethods, methodMeta } from './BranchesContext';
import { BranchHoursSection } from './BranchHoursSection';
import { BranchStaffSection } from './BranchStaffSection';
import { BranchStationsSection } from './BranchStationsSection';
import { BranchOutletsSection } from './BranchOutletsSection';
import { BranchOrderingSection } from './BranchOrderingSection';
import { PaymentMethod, paymentMethodLabel } from './OrdersContext';
import { useMenus, catalogItems } from './MenusContext';
const methodIcons: Record<keyof BranchOrderMethods, ComponentType<{
  size?: number;
}>> = {
  dineIn: QrCode,
  takeout: ShoppingCart,
  delivery: Bike,
  orderAhead: CalendarClock
};
const colorMap: Record<string, {
  ring: string;
  bg: string;
  text: string;
  chip: string;
  iconBg: string;
}> = {
  purple: {
    ring: 'ring-purple-200',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    chip: 'bg-purple-100 text-purple-700',
    iconBg: 'bg-purple-100 text-purple-600'
  },
  orange: {
    ring: 'ring-orange-200',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    chip: 'bg-orange-100 text-orange-700',
    iconBg: 'bg-orange-100 text-orange-600'
  },
  blue: {
    ring: 'ring-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    chip: 'bg-blue-100 text-blue-700',
    iconBg: 'bg-blue-100 text-blue-600'
  },
  emerald: {
    ring: 'ring-emerald-200',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    chip: 'bg-emerald-100 text-emerald-700',
    iconBg: 'bg-emerald-100 text-emerald-600'
  }
};
export function BranchesPage() {
  const {
    branches,
    menus,
    updateBranch,
    addBranch,
    deleteBranch
  } = useBranches();
  const {
    branchOverrides,
    setBranchItemAvailability,
    setBranchItemPrice
  } = useMenus();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(branches[0]?.id || null);
  const [search, setSearch] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const filtered = useMemo(() => {
    if (!search.trim()) return branches;
    const q = search.toLowerCase();
    return branches.filter(b => b.name.toLowerCase().includes(q) || b.address.toLowerCase().includes(q) || (b.manager || '').toLowerCase().includes(q));
  }, [branches, search]);
  const selected = branches.find(b => b.id === selectedId) || filtered[0] || null;
  const flashSaved = () => {
    setSavedFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1200);
  };
  const update = (patch: Partial<Branch>) => {
    if (!selected) return;
    updateBranch(selected.id, patch);
    flashSaved();
  };
  const handleAdd = () => {
    const id = addBranch();
    setSelectedId(id);
  };
  const handleDelete = (id: string) => {
    if (!window.confirm('Delete this branch? This cannot be undone.')) return;
    deleteBranch(id);
    setSelectedId(branches.find(b => b.id !== id)?.id || null);
  };
  const activeCount = branches.filter(b => b.active).length;
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-304">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-305">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-306">
          <MenuIcon size={24} data-id="element-307" />
        </button>
        <span className="font-semibold" data-id="element-308">Branches</span>
        <div className="w-6" data-id="element-309" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-310" />

      <main className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-311">
        {/* Header */}
        <div className="px-4 md:px-6 pt-5 md:pt-6 pb-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-312">
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap" data-id="element-313">
            <div data-id="element-314">
              <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1 flex items-center gap-1.5" data-id="element-315">
                <Building2 size={13} data-id="element-316" />
                Operations
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-317">Branches</h1>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-318">
                {branches.length} branches · {activeCount} active
              </p>
            </div>
            <button onClick={handleAdd} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm" data-id="element-319">
              <Plus size={16} data-id="element-320" />
              Add branch
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[360px_1fr]" data-id="element-321">
          {/* List */}
          <div className="overflow-y-auto border-r border-gray-200 bg-gray-50/40 p-4 space-y-2" data-id="element-322">
            <div className="relative mb-2" data-id="element-323">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-324" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search branches..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-325" />
            </div>
            {filtered.map(b => <BranchCard key={b.id} branch={b} isSelected={selected?.id === b.id} onClick={() => setSelectedId(b.id)} data-id="element-326" />)}
            {filtered.length === 0 && <div className="p-8 text-center text-sm text-gray-500" data-id="element-327">
                No branches match your search.
              </div>}
          </div>

          {/* Detail */}
          <div className="overflow-y-auto bg-white" data-id="element-328">
            {selected ? (() => {
            // Alias the selected branch + its update/delete handlers into the
            // local scope so the JSX block below (originally lifted from a
            // BranchDetail helper) can keep using `branch`, `onUpdate`, and
            // `onDelete` without redeclaration.
            const branch = selected;
            const onUpdate = update;
            const onDelete = () => handleDelete(selected.id);
            return <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-8 pb-24" data-id="element-329">
                    <div className="flex items-center justify-between border-b pb-4" data-id="element-330">
                      <div className="flex-1 min-w-0" data-id="element-331">
                        <input value={branch.name} onChange={e => onUpdate({
                    name: e.target.value
                  })} className="text-2xl font-bold text-gray-900 bg-transparent outline-none w-full -ml-1 px-1 rounded-md focus:bg-orange-50 focus:ring-2 focus:ring-orange-100 transition" data-id="element-332" />
                        <div className="text-xs text-gray-500 mt-1" data-id="element-333">
                          Created{' '}
                          {new Date(branch.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-334">
                        <AnimatePresence data-id="element-335">
                          {savedFlash && <motion.span initial={{
                      opacity: 0,
                      y: -4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-emerald-100 text-emerald-700" data-id="element-336">
                              <Check size={11} strokeWidth={3} data-id="element-337" />
                              Saved
                            </motion.span>}
                        </AnimatePresence>
                        <button onClick={onDelete} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete branch" aria-label="Delete branch" data-id="element-338">
                          <Trash2 size={16} data-id="element-339" />
                        </button>
                      </div>
                    </div>

                    {/* Live toggle (prominent) */}
                    <div className={`flex items-center justify-between gap-4 rounded-xl border p-4 mb-6 ${branch.active ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-340">
                      <div className="flex items-center gap-3" data-id="element-341">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${branch.active ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-342">
                          <Power size={18} data-id="element-343" />
                        </div>
                        <div data-id="element-344">
                          <div className="font-bold text-gray-900 text-sm" data-id="element-345">
                            {branch.active ? 'Taking orders' : 'Paused'}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-346">
                            {branch.active ? 'Customers can place orders at this branch' : 'New orders are paused for this branch'}
                          </div>
                        </div>
                      </div>
                      <Toggle checked={branch.active} onChange={v => onUpdate({
                  active: v
                })} data-id="element-347" />
                    </div>

                    {/* Basic info */}
                    <FormSection title="Basic information" data-id="element-348">
                      <Field label="Address" icon={<MapPin size={14} data-id="element-350" />} data-id="element-349">
                        <textarea value={branch.address} onChange={e => onUpdate({
                    address: e.target.value
                  })} placeholder="Street address, district, city" rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition resize-none" data-id="element-351" />
                      </Field>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-352">
                        <Field label="Phone number" icon={<Phone size={14} data-id="element-354" />} data-id="element-353">
                          <input type="tel" value={branch.phone} onChange={e => onUpdate({
                      phone: e.target.value
                    })} placeholder="+251 911 000 000" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-355" />
                        </Field>
                        <Field label="Email" icon={<Mail size={14} data-id="element-357" />} data-id="element-356">
                          <input type="email" value={branch.email} onChange={e => onUpdate({
                      email: e.target.value
                    })} placeholder="branch@example.com" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-358" />
                        </Field>
                      </div>
                      <Field label="Branch manager" icon={<User size={14} data-id="element-360" />} data-id="element-359">
                        <input value={branch.manager || ''} onChange={e => onUpdate({
                    manager: e.target.value
                  })} placeholder="Name of the manager" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-361" />
                      </Field>
                    </FormSection>

                    {/* Menu */}
                    <FormSection title="Default menu" description="Which menu is shown to customers at this branch" data-id="element-362">
                      <Field label="Menu" icon={<ChefHat size={14} data-id="element-364" />} data-id="element-363">
                        <div className="relative" data-id="element-365">
                          <select value={branch.defaultMenuId} onChange={e => onUpdate({
                      defaultMenuId: e.target.value
                    })} className="w-full appearance-none pl-3 pr-10 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-366">
                            {menus.map(m => <option key={m.id} value={m.id} data-id="element-367">
                                {m.name}
                              </option>)}
                          </select>
                          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-368" />
                        </div>
                      </Field>
                    </FormSection>

                    {/* Ordering & QR Codes */}
                    <FormSection title="Ordering & QR Codes" description="Configure how customers can order and generate the QR codes they'll scan." data-id="element-369">
                      <BranchOrderingSection branch={branch} onUpdate={onUpdate} data-id="element-370" />
                    </FormSection>

                    {/* Taxes & Fees */}
                    <FormSection title="Taxes & Fees" description="Tax rate and service fee applied to orders at this branch" data-id="element-371">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-372">
                        <Field label="Tax Rate (%)" icon={<Percent size={14} data-id="element-374" />} data-id="element-373">
                          <input type="number" value={branch.taxRatePct} onChange={e => onUpdate({
                      taxRatePct: Number(e.target.value) || 0
                    })} min="0" max="100" step="0.1" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-375" />
                        </Field>
                        <Field label="Service Fee (%)" icon={<Percent size={14} data-id="element-377" />} data-id="element-376">
                          <input type="number" value={branch.serviceFeePct} onChange={e => onUpdate({
                      serviceFeePct: Number(e.target.value) || 0
                    })} min="0" max="100" step="0.1" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-378" />
                        </Field>
                      </div>
                    </FormSection>

                    {/* Payment Methods */}
                    <FormSection title="Payment Methods" description="Which payment methods customers can use at this branch" data-id="element-379">
                      <div className="grid grid-cols-2 gap-4" data-id="element-380">
                        {(Object.keys(branch.acceptedPaymentMethods) as PaymentMethod[]).map(key => <div key={key} className="flex items-center justify-between p-3 rounded-xl border border-gray-200 bg-white" data-id="element-381">
                            <span className="text-sm font-semibold text-gray-700" data-id="element-382">
                              {paymentMethodLabel[key]}
                            </span>
                            <Toggle checked={branch.acceptedPaymentMethods[key]} onChange={v => onUpdate({
                      acceptedPaymentMethods: {
                        ...branch.acceptedPaymentMethods,
                        [key]: v
                      }
                    })} data-id="element-383" />
                          </div>)}
                      </div>
                    </FormSection>

                    {/* Bank Accounts */}
                    {branch.acceptedPaymentMethods['bank-transfer'] && <FormSection title="Bank Accounts" description="Manage bank accounts for direct transfers" data-id="element-384">
                        <div className="space-y-3" data-id="element-385">
                          {branch.bankAccounts?.map((account, idx) => <div key={account.id} className="flex items-start justify-between p-4 rounded-xl border border-gray-200 bg-white gap-4" data-id="element-386">
                              <div className="flex-1 space-y-3" data-id="element-387">
                                <div className="grid grid-cols-2 gap-3" data-id="element-388">
                                  <div data-id="element-389">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-390">
                                      Bank Name
                                    </label>
                                    <input value={account.bankName} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              bankName: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. CBE" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-391" />
                                  </div>
                                  <div data-id="element-392">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-393">
                                      Label (Optional)
                                    </label>
                                    <input value={account.label || ''} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              label: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. Main Account" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-394" />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3" data-id="element-395">
                                  <div data-id="element-396">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-397">
                                      Account Name
                                    </label>
                                    <input value={account.accountName} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              accountName: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. Demo Restaurant PLC" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-398" />
                                  </div>
                                  <div data-id="element-399">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-400">
                                      Account Number
                                    </label>
                                    <input value={account.accountNumber} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              accountNumber: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. 1000123456789" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-401" />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 pt-1" data-id="element-402">
                                  <Toggle checked={account.active} onChange={v => {
                          const newAccounts = [...(branch.bankAccounts || [])];
                          newAccounts[idx] = {
                            ...account,
                            active: v
                          };
                          onUpdate({
                            bankAccounts: newAccounts
                          });
                        }} data-id="element-403" />
                                  <span className="text-xs text-gray-600 font-medium" data-id="element-404">
                                    Active
                                  </span>
                                </div>
                              </div>
                              <button onClick={() => {
                      const newAccounts = branch.bankAccounts?.filter((_, i) => i !== idx);
                      onUpdate({
                        bankAccounts: newAccounts
                      });
                    }} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-405">
                                <Trash2 size={16} data-id="element-406" />
                              </button>
                            </div>)}
                          <button onClick={() => {
                    const newAccount = {
                      id: `ba_${Date.now().toString(36)}`,
                      bankName: '',
                      accountName: '',
                      accountNumber: '',
                      active: true
                    };
                    onUpdate({
                      bankAccounts: [...(branch.bankAccounts || []), newAccount]
                    });
                  }} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm font-bold text-gray-500 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2" data-id="element-407">
                            <Plus size={16} data-id="element-408" />
                            Add Bank Account
                          </button>
                        </div>
                      </FormSection>}

                    {/* Customer tipping */}
                    <FormSection title="Customer tipping" description="Show customers tip presets at checkout for this branch" data-id="element-409">
                      <div className="space-y-4" data-id="element-410">
                        <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200" data-id="element-411">
                          <div className="min-w-0" data-id="element-412">
                            <div className="text-sm font-semibold text-gray-900" data-id="element-413">
                              Enable customer tipping
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-414">
                              When on, customers see tip presets at checkout for
                              this branch.
                            </div>
                          </div>
                          <Toggle checked={!!branch.tippingOverride} onChange={v => onUpdate({
                      tippingOverride: v ? {
                        customerTippingEnabled: true,
                        suggestedTipPcts: [10, 15, 20]
                      } : undefined
                    })} data-id="element-415" />
                        </div>

                        {branch.tippingOverride && <div data-id="element-416">
                            <div className="grid grid-cols-3 gap-3" data-id="element-417">
                              {[0, 1, 2].map(idx => <Field key={idx} label={`Preset ${idx + 1} (%)`} data-id="element-418">
                                  <input type="number" value={branch.tippingOverride!.suggestedTipPcts[idx] ?? 0} onChange={e => {
                          const next = [...branch.tippingOverride!.suggestedTipPcts];
                          next[idx] = Number(e.target.value) || 0;
                          onUpdate({
                            tippingOverride: {
                              ...branch.tippingOverride!,
                              suggestedTipPcts: next
                            }
                          });
                        }} min="0" max="100" step="1" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-419" />
                                </Field>)}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2" data-id="element-420">
                              Shown to customers as quick-pick chips at
                              checkout. They can also enter a custom amount.
                            </p>
                          </div>}
                      </div>
                    </FormSection>

                    {/* Menu & Pricing */}
                    <FormSection title="Menu & Pricing" description="Manage which items are available at this branch and set branch-specific prices." data-id="element-421">
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-422">
                        <div className="grid grid-cols-[1fr_auto_120px] gap-4 p-4 border-b border-gray-100 bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-423">
                          <div data-id="element-424">Item</div>
                          <div className="text-center" data-id="element-425">Available</div>
                          <div className="text-right" data-id="element-426">Branch Price</div>
                        </div>
                        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto" data-id="element-427">
                          {catalogItems.map(item => {
                      const override = branchOverrides[branch.id]?.[item.id] || {};
                      const isAvailable = override.available ?? true;
                      const price = override.price ?? item.price;
                      return <div key={item.id} className={`grid grid-cols-[1fr_auto_120px] gap-4 p-4 items-center transition-colors ${!isAvailable ? 'bg-gray-50 opacity-60' : ''}`} data-id="element-428">
                                <div data-id="element-429">
                                  <div className="font-bold text-sm text-gray-900" data-id="element-430">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-431">
                                    {item.category}
                                  </div>
                                </div>
                                <div className="flex justify-center" data-id="element-432">
                                  <Toggle checked={isAvailable} onChange={v => setBranchItemAvailability(branch.id, item.id, v)} data-id="element-433" />
                                </div>
                                <div data-id="element-434">
                                  <div className="relative" data-id="element-435">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium" data-id="element-436">
                                      ETB
                                    </span>
                                    <input type="number" value={override.price !== undefined ? override.price : ''} placeholder={item.price.toString()} onChange={e => {
                              const val = e.target.value;
                              setBranchItemPrice(branch.id, item.id, val ? Number(val) : undefined);
                            }} disabled={!isAvailable} className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm font-bold tabular-nums focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:bg-gray-100" data-id="element-437" />
                                  </div>
                                </div>
                              </div>;
                    })}
                        </div>
                      </div>
                    </FormSection>

                    {/* Outlets & Concepts */}
                    <FormSection title="Outlets & Concepts" description="Manage distinct dining concepts or brands within this location, each with its own customer menu and QR code." data-id="element-438">
                      <BranchOutletsSection branch={branch} data-id="element-439" />
                    </FormSection>

                    {/* Operating Hours */}
                    <FormSection title="Operating Hours" description="Operating hours, per-method overrides, holidays and temporary closures" data-id="element-440">
                      <BranchHoursSection branch={branch} onUpdate={onUpdate} data-id="element-441" />
                    </FormSection>

                    {/* Kitchen stations */}
                    <FormSection title="Kitchen stations" description="Prep areas at this branch. Items route to one station; the kitchen display can be viewed per-station or as an expediter overview." data-id="element-442">
                      <BranchStationsSection branch={branch} data-id="element-443" />
                    </FormSection>

                    {/* Staff & permissions */}
                    <FormSection title="Staff & permissions" description="Assign managers and staff with branch-level access and SMS routing" data-id="element-444">
                      <BranchStaffSection branch={branch} data-id="element-445" />
                    </FormSection>

                    {/* Reservations */}
                    <FormSection title="Reservations" description="Allow customers to book tables in advance" data-id="element-446">
                      <ReservationsRow enabled={branch.reservationsEnabled} onChange={v => onUpdate({
                  reservationsEnabled: v
                })} data-id="element-447" />
                    </FormSection>
                  </div>;
          })() : <div className="h-full flex items-center justify-center p-10 text-center" data-id="element-448">
                <div data-id="element-449">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-450">
                    <Building2 size={22} className="text-gray-400" data-id="element-451" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-452">
                    No branches yet
                  </h3>
                  <p className="text-xs text-gray-500 mb-3" data-id="element-453">
                    Add a branch to get started.
                  </p>
                  <button onClick={handleAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors" data-id="element-454">
                    <Plus size={14} data-id="element-455" />
                    Add branch
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </main>
    </div>;
}
function BranchCard({
  branch,
  isSelected,
  onClick
}: {
  branch: Branch;
  isSelected: boolean;
  onClick: () => void;
}) {
  const enabledMethods = Object.values(branch.methods).filter(Boolean).length;
  return <motion.button layout onClick={onClick} className={`w-full text-left p-4 rounded-xl border transition-colors relative ${isSelected ? 'bg-white border-orange-300 ring-2 ring-orange-100 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-456">
      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-457">
        <div className="flex-1 min-w-0" data-id="element-458">
          <div className="flex items-center gap-2" data-id="element-459">
            <h3 className="font-bold text-gray-900 text-[14px] truncate" data-id="element-460">
              {branch.name}
            </h3>
          </div>
          {branch.address && <div className="flex items-start gap-1 text-[11px] text-gray-500 mt-1" data-id="element-461">
              <MapPin size={11} className="flex-shrink-0 mt-0.5" data-id="element-462" />
              <span className="truncate" data-id="element-463">{branch.address}</span>
            </div>}
        </div>
        <BranchStatusPill branch={branch} data-id="element-464" />
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500" data-id="element-465">
        <div className="flex items-center gap-1" data-id="element-466">
          <span className="font-semibold text-gray-700" data-id="element-467">{enabledMethods}</span>
          method{enabledMethods === 1 ? '' : 's'} on
          {branch.reservationsEnabled && <>
              <span className="text-gray-300" data-id="element-468">·</span>
              <Calendar size={11} className="text-blue-500" data-id="element-469" />
              <span className="text-blue-700 font-semibold" data-id="element-470">Reservations</span>
            </>}
        </div>
        {branch.manager && <span className="truncate text-gray-400" data-id="element-471">{branch.manager}</span>}
      </div>
    </motion.button>;
}
function BranchStatusPill({
  branch
}: {
  branch: Branch;
}) {
  if (branch.temporaryClosure?.active) {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700" data-id="element-472">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" data-id="element-473" />
        Closed
      </span>;
  }
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${branch.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-474">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${branch.active ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-475" />
      {branch.active ? 'Live' : 'Paused'}
    </span>;
}
function FormSection({
  title,
  description,
  children
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return <section className="mb-7" data-id="element-476">
      <div className="mb-3" data-id="element-477">
        <h3 className="font-bold text-gray-900 text-sm" data-id="element-478">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-479">{description}</p>}
      </div>
      <div className="space-y-4" data-id="element-480">{children}</div>
    </section>;
}
function Field({
  label,
  icon,
  children
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-481">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-482">
        {icon}
        {label}
      </div>
      {children}
    </label>;
}
function ReservationsRow({
  enabled,
  onChange
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${enabled ? 'border-transparent bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 bg-white'}`} data-id="element-483">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-484">
        <Calendar size={16} data-id="element-485" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-486">
        <div className="flex items-center justify-between gap-2" data-id="element-487">
          <span className={`font-bold text-sm ${enabled ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-488">
            Accept reservations
          </span>
          <Toggle checked={enabled} onChange={onChange} data-id="element-489" />
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5 leading-snug" data-id="element-490">
          Customers can book tables ahead through the customer menu
        </p>
      </div>
    </div>;
}